import type { ReportTemplate } from '~/gql/reports/reports'
import { Copy, Download, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { tourAnchor } from '~/helpers/tour/anchors'
import { scheduleSummary } from './schedule-form'

type Props = {
  templates: ReportTemplate[]
  /** Modifier, dupliquer, supprimer : réservés à l'admin, comme côté API. */
  canManage: boolean
  downloadingId: string | null
  onDownload: (template: ReportTemplate) => void
  onEdit: (template: ReportTemplate) => void
  onDuplicate: (template: ReportTemplate) => void
  onRemove: (template: ReportTemplate) => void | Promise<void>
}

/** Les modèles de rapport de l'organisation (#163) et leurs actions. */
function ReportTemplatesList({ templates, canManage, downloadingId, onDownload, onEdit, onDuplicate, onRemove }: Props) {
  const { t } = useTranslation()
  const [removing, setRemoving] = useState<ReportTemplate | null>(null)

  return (
    <Card {...tourAnchor('reports.list')}>
      <CardHeader>
        <CardTitle>{t('reports.list.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {templates.length === 0
          ? <p className="text-sm text-muted-foreground">{t(canManage ? 'reports.list.emptyAdmin' : 'reports.list.empty')}</p>
          : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('reports.list.name')}</TableHead>
                    <TableHead>{t('reports.list.granularity')}</TableHead>
                    <TableHead>{t('reports.list.columns')}</TableHead>
                    <TableHead>{t('reports.list.schedule')}</TableHead>
                    <TableHead {...tourAnchor('reports.download')}><span className="sr-only">{t('reports.list.actions')}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(template => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>{t(`reports.granularity.${template.granularity}`)}</TableCell>
                      <TableCell>{template.columns.length}</TableCell>
                      <TableCell>{scheduleSummary(template.schedule, t)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button type="button" variant="outline" size="sm" disabled={downloadingId === template.id} onClick={() => onDownload(template)}>
                            <Download className="mr-1 size-4" />
                            {t('reports.list.download')}
                          </Button>
                          {canManage && (
                            <>
                              <Button type="button" variant="ghost" size="icon" aria-label={t('reports.list.edit', { name: template.name })} onClick={() => onEdit(template)}>
                                <Pencil className="size-4" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" aria-label={t('reports.list.duplicate', { name: template.name })} onClick={() => onDuplicate(template)}>
                                <Copy className="size-4" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" aria-label={t('reports.list.delete', { name: template.name })} onClick={() => setRemoving(template)}>
                                <Trash2 className="size-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </CardContent>
      <ConfirmDialog
        open={removing != null}
        title={t('reports.list.deleteTitle', { name: removing?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) void onRemove(removing)
          setRemoving(null)
        }}
      >
        {t('reports.list.deleteBody')}
      </ConfirmDialog>
    </Card>
  )
}

export default ReportTemplatesList
